import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Zap, Activity, Clock, CheckCircle2, Shield } from 'lucide-react';

const PILLARS = [
  {
    icon: Zap,
    title: "Efficiency",
    points: [
      "Automated video segmentation, defect analysis and regulatory report generation",
      "One platform, multiple airframes, dual use (Commercial & Defense)",
      "Same headcount, increased capacity, consistent and repeatable"
    ]
  },
  {
    icon: Activity,
    title: "Traceability",
    points: [
      "No missing historical data — past paper-based inspections converted into digital history",
      "Chain-of-custody for every frame, from tip to tail",
      "Tail number to entire fleet actionable intelligence"
    ]
  },
  {
    icon: Clock,
    title: "Turnaround Time",
    points: [
      "Actionable inspection results in less than 1 hour",
      "Perform multiple inspections simultaneously on multiple aircraft",
      "Complete inspections anywhere — inside or outside the hangar, at the gate, or in the middle of nowhere"
    ]
  }
];

const COMPLIANCE_BADGES = [
  "FAA-Compliant",
  "EASA-Compliant",
  "Compliant with AI-Frameworks for both FAA & EASA",
  "ITAR-Compliant",
  "EAR-Compliant"
];

export const Solution = () => {
  return (
    <section id="solution" data-testid="solution-section" className="py-20 px-6 bg-white">
      <div className="container mx-auto max-w-6xl">
        {/* Intro paragraph */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <Badge className="bg-orange-100 text-orange-700 border-orange-200 mb-4">
            Solution
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
            <span className="text-orange-600">ArcheanX</span> as a <span className="text-orange-600">DUAL USE</span> with sub-millimeter precision surface anomaly detection
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mt-6">
            Local edge AI, hardware-agnostic, across commercial MROs and military flightlines. Plugs natively into existing MRO enterprise software <span className="font-semibold text-gray-900">(AMOS, Maintenix, SAP)</span> to auto-trigger repair work orders.
          </p>
        </div>

        {/* Three Pillars */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {PILLARS.map((pillar, idx) => {
            const Icon = pillar.icon;
            return (
              <Card
                key={idx}
                data-testid={`pillar-card-${idx}`}
                className="border-gray-200 bg-white hover:shadow-xl hover:border-orange-200 transition-all duration-300"
              >
                <CardContent className="p-8 h-full flex flex-col">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-5">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-black mb-4">{pillar.title}</h3>
                  <ul className="space-y-3 flex-grow">
                    {pillar.points.map((point, pIdx) => (
                      <li key={pIdx} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700 leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Compliance Strip */}
        <div className="border-t border-gray-200 pt-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-orange-600" />
              <span className="text-sm font-semibold uppercase tracking-wider text-orange-600">Compliance</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-black">Built for global aviation & defense standards</h3>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {COMPLIANCE_BADGES.map((label, idx) => (
              <div
                key={idx}
                data-testid={`compliance-badge-${idx}`}
                className="flex items-center gap-2 border border-orange-200 bg-orange-50 rounded-full px-5 py-2.5 hover:bg-orange-100 transition-colors"
              >
                <CheckCircle2 className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-gray-900">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
